import React, { useState, useEffect } from "react";
import {
    Settings2, Type, Palette, Highlighter, RotateCcw, Sun, Moon, Coffee,
    Sparkles, Loader2, RefreshCw, ScrollText, Gauge,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";
import { READER_FONTS, READER_THEMES, ANCHOR_MODES } from "@/components/reader/readerPrefs";
import { anchorSegments, availableCategories, HIGHLIGHT_PRESETS, presetForLayers, presetLayers, recommendedPreset } from "@/components/reader/readingEngine";
import { HL_SWATCH } from "@/components/reader/HighlightControls";

const THEME_ICON = { night: Moon, day: Sun, sepia: Coffee };

const Row = ({ label, children }) => (
    <div className="flex items-center justify-between gap-3 py-1.5">
        <span className="text-sm text-t2">{label}</span>
        <div className="flex items-center gap-1.5">{children}</div>
    </div>
);

const Seg = ({ options, value, onChange, testidPrefix }) => (
    <div className="flex flex-wrap items-center gap-1 rounded-lg bg-surface-2 p-0.5">
        {options.map((o) => (
            <button
                key={o.value}
                data-testid={testidPrefix ? `${testidPrefix}-${o.value}` : undefined}
                onClick={() => onChange(o.value)}
                className={cn(
                    "rounded-md px-2.5 py-1 text-xs transition-colors",
                    value === o.value ? "bg-surface-3 text-t1 shadow-elev-1" : "text-t3 hover:text-t1"
                )}
            >
                {o.icon ? <o.icon className="h-3.5 w-3.5" /> : o.label}
            </button>
        ))}
    </div>
);

const Group = ({ icon: Icon, title, testid, children }) => (
    <div className="border-b border-border/60 px-4 py-3 last:border-b-0" data-testid={testid}>
        <div className="mb-2 flex items-center gap-2">
            <Icon className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
            <span className="mono-label text-t3">{title}</span>
        </div>
        <div className="space-y-0.5">{children}</div>
    </div>
);

const AnchorPreview = ({ mode }) => {
    const word = "Reading";
    const seg = mode !== "off" ? anchorSegments(word, mode) : null;
    return (
        <span className="font-reading text-sm text-t2" data-testid="anchor-preview">
            {seg ? (<><b className="text-[hsl(48,95%,58%)]">{seg.head}</b>{seg.mid}{seg.last && <b className="text-[hsl(48,95%,58%)]">{seg.last}</b>}</>) : word}
        </span>
    );
};

// One-click, outcome-based Smart Highlight presets. They only combine existing
// cached layers (no new AI call). A domain-matched preset is gently recommended
// but never auto-applied.
const HighlightPresets = ({ hl, t }) => {
    const active = presetForLayers(hl.layers);
    const rec = recommendedPreset(hl.data);
    return (
        <div className="mb-2.5" data-testid="rset-presets">
            <div className="mono-label mb-1.5 text-t4">{t("rset.presets.label")}</div>
            <div className="flex flex-wrap gap-1.5">
                {HIGHLIGHT_PRESETS.map((p) => (
                    <button
                        key={p.id}
                        data-testid={`rset-preset-${p.id}`}
                        onClick={() => hl.applyLayers(presetLayers(p.id))}
                        className={cn(
                            "rounded-full border px-2.5 py-1 text-xs transition-colors",
                            active === p.id ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/12 text-t1" : "border-border bg-surface-2 text-t3 hover:text-t1"
                        )}
                    >
                        {t(`rset.presets.${p.id}`)}
                    </button>
                ))}
                <span
                    data-testid="rset-preset-custom"
                    className={cn(
                        "rounded-full border px-2.5 py-1 text-xs",
                        active === "custom" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/12 text-t1" : "border-dashed border-border bg-transparent text-t4"
                    )}
                >
                    {t("rset.presets.custom")}
                </span>
            </div>
            {rec && active !== rec && (
                <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/10 px-2.5 py-1.5" data-testid="rset-preset-recommend">
                    <span className="min-w-0 text-xs text-t3">
                        <span className="text-[hsl(var(--primary))]">{t("rset.presets.recommend")}:</span> {t(`rset.presets.${rec}`)}
                    </span>
                    <button
                        data-testid="rset-preset-recommend-apply"
                        onClick={() => hl.applyLayers(presetLayers(rec))}
                        className="shrink-0 rounded-md bg-surface-3 px-2 py-0.5 text-[11px] text-t1 transition-colors hover:bg-surface-3/70"
                    >
                        {t("rset.presets.apply")}
                    </button>
                </div>
            )}
        </div>
    );
};

const SettingsBody = ({ settings: s, movement, focus, guided, hl, t }) => {
    const themeOpts = READER_THEMES.map((v) => ({ value: v, icon: THEME_ICON[v] }));
    const anchorOpts = ANCHOR_MODES.map((v) => ({ value: v, label: t(`rset.anchor.${v}`) }));
    const cats = hl ? availableCategories(hl.data) : [];

    return (
        <div className="max-h-[70vh] overflow-y-auto sm:max-h-[520px]">
            {/* Bewegung */}
            {(movement || focus || guided) && (
                <Group icon={Gauge} title={t("rset.movement")} testid="rset-group-movement">
                    {guided && (
                        <>
                            <Row label={t("rset.readingFocus")}>
                                <Seg
                                    testidPrefix="rset-focus"
                                    value={guided.focus}
                                    onChange={guided.setFocus}
                                    options={[
                                        { value: "minimal", label: t("rset.focusMode.minimal") },
                                        { value: "word", label: t("rset.focusMode.word") },
                                        { value: "trail", label: t("rset.focusMode.trail") },
                                    ]}
                                />
                            </Row>
                            {guided.focus === "trail" && (
                                <Row label={t("rset.trailLength")}>
                                    <Seg
                                        testidPrefix="rset-trail"
                                        value={guided.trailLength}
                                        onChange={guided.setTrailLength}
                                        options={[
                                            { value: "short", label: t("rset.trailLen.short") },
                                            { value: "medium", label: t("rset.trailLen.medium") },
                                            { value: "long", label: t("rset.trailLen.long") },
                                        ]}
                                    />
                                </Row>
                            )}
                        </>
                    )}
                    {movement?.grains && (
                        <Row label={t("rset.granularity")}>
                            <Seg
                                testidPrefix="rset-grain"
                                value={movement.granularity}
                                onChange={movement.setGranularity}
                                options={movement.grains.map((g) => ({ value: g, label: t(`rset.grain.${g}`) }))}
                            />
                        </Row>
                    )}
                    <Row label={t("rset.autoScroll")}>
                        <Switch data-testid="rset-autoscroll" checked={s.autoScroll} onCheckedChange={s.setAutoScroll} />
                    </Row>
                    {focus && (
                        <Row label={t("rset.dim")}>
                            <Switch data-testid="rset-dim" checked={focus.dim} onCheckedChange={focus.setDim} />
                        </Row>
                    )}
                </Group>
            )}

            {/* Darstellung */}
            <Group icon={Palette} title={t("rset.display")} testid="rset-group-appearance">
                <Row label={t("rset.theme")}>
                    <Seg testidPrefix="rset-theme" value={s.readerTheme} onChange={s.setTheme} options={themeOpts} />
                </Row>
                <div className="py-1.5">
                    <span className="mb-1.5 block text-sm text-t2">{t("rset.font")}</span>
                    <div className="grid grid-cols-1 gap-1">
                        {READER_FONTS.map((f) => (
                            <button
                                key={f.id}
                                data-testid={`rset-font-${f.id}`}
                                onClick={() => s.setFont(f.id)}
                                style={{ fontFamily: f.stack }}
                                className={cn(
                                    "flex items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                                    s.readerFont === f.id ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-t1" : "border-border bg-surface-2 text-t2 hover:text-t1"
                                )}
                            >
                                <span>{f.label}</span>
                                <span className="mono-label text-t4">{t(`rset.fontNote.${f.note}`)}</span>
                            </button>
                        ))}
                    </div>
                </div>
                <Row label={`${t("rset.size")} · ${s.fontSize}px`}>
                    <div className="w-28"><Slider data-testid="rset-fontsize" value={[s.fontSize]} min={16} max={26} step={1} onValueChange={(v) => s.setFontSize(v[0])} /></div>
                </Row>
                <Row label={t("rset.width")}>
                    <Seg testidPrefix="rset-width" value={s.textWidth} onChange={s.setTextWidth}
                        options={[{ value: "narrow", label: t("rset.wNarrow") }, { value: "standard", label: t("rset.wStandard") }, { value: "wide", label: t("rset.wWide") }]} />
                </Row>
                <Row label={t("rset.lineHeight")}>
                    <Seg testidPrefix="rset-lh" value={s.lineHeightMode} onChange={s.setLineHeightMode}
                        options={[{ value: "compact", label: t("rset.lhCompact") }, { value: "standard", label: t("rset.lhStandard") }, { value: "airy", label: t("rset.lhAiry") }]} />
                </Row>
                <Row label={t("rset.textAlign")}>
                    <Seg testidPrefix="rset-align" value={s.textAlign} onChange={s.setTextAlign}
                        options={[{ value: "left", label: t("rset.alignLeft") }, { value: "justify", label: t("rset.alignJustify") }]} />
                </Row>
                <button data-testid="rset-reset" onClick={s.reset} className="mt-1.5 flex items-center gap-1.5 text-xs text-t4 transition-colors hover:text-t2">
                    <RotateCcw className="h-3 w-3" /> {t("rset.reset")}
                </button>
            </Group>

            {/* Hervorhebung */}
            <Group icon={Highlighter} title={t("rset.highlight")} testid="rset-group-highlighting">
                <div className="rounded-lg bg-surface-2/60 px-3 py-2">
                    <div className="flex items-center gap-2 text-sm text-t2">
                        <span className="h-2.5 w-2.5 rounded-full bg-[hsl(48,100%,60%)]" />
                        {t("rset.activeWord")}
                    </div>
                    <p className="mt-0.5 text-xs text-t4">{t("rset.activeWordHint")}</p>
                </div>

                {/* Word Anchor */}
                <div className="mt-2 py-1">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-t2">{t("rset.wordAnchor")}</span>
                        <AnchorPreview mode={s.wordAnchor} />
                    </div>
                    <div className="mt-1.5"><Seg testidPrefix="rset-anchor" value={s.wordAnchor} onChange={s.setWordAnchor} options={anchorOpts} /></div>
                    {s.wordAnchor !== "off" && (
                        <Row label={`${t("rset.intensity")} · ${Math.round(s.anchorIntensity * 100)}%`}>
                            <div className="w-28"><Slider data-testid="rset-anchor-intensity" value={[s.anchorIntensity]} min={0.4} max={1} step={0.05} onValueChange={(v) => s.setAnchorIntensity(v[0])} /></div>
                        </Row>
                    )}
                </div>

                {/* AI Smart Highlights */}
                {hl && (
                    <div className="mt-2 border-t border-border/60 pt-2">
                        <Row label={t("rset.smartHighlights")}>
                            <Switch data-testid="rset-highlights-master" checked={hl.enabled} onCheckedChange={hl.setEnabled} />
                        </Row>
                        {hl.enabled && hl.data && !hl.loading && (
                            <p className="mb-2 px-1 text-xs leading-snug text-t4">{t("rset.smartHint")}</p>
                        )}
                        {hl.enabled && hl.loading && (
                            <div className="flex items-center gap-2 px-1 py-3 text-sm text-t3"><Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--primary))]" /> {t("rset.analyzing")}</div>
                        )}
                        {hl.enabled && hl.error && !hl.loading && (
                            <div className="px-1 py-2">
                                <p className="text-xs text-t3">{hl.error}</p>
                                <Button data-testid="rset-highlights-retry" size="sm" variant="secondary" className="mt-1.5 gap-1.5 bg-surface-3/60" onClick={hl.retry}><RefreshCw className="h-3.5 w-3.5" /> {t("rset.generate")}</Button>
                            </div>
                        )}
                        {hl.enabled && !hl.loading && !hl.error && !hl.data && (
                            <div className="px-1 py-2">
                                <p className="text-xs text-t4">{t("rset.noHighlightsYet")}</p>
                                <Button data-testid="rset-highlights-generate" size="sm" className="mt-1.5 gap-1.5" onClick={hl.retry}><Sparkles className="h-3.5 w-3.5" /> {t("rset.generate")}</Button>
                            </div>
                        )}
                        {hl.enabled && !hl.loading && !hl.error && hl.data && cats.length === 0 && (
                            <p className="px-1 py-3 text-center text-sm text-t4">{t("rset.noHighlights")}</p>
                        )}
                        {hl.enabled && !hl.loading && hl.data && cats.length > 0 && (
                            <>
                                <HighlightPresets hl={hl} t={t} />
                                <div className="space-y-0.5">
                                    {cats.map((cat) => {
                                        const label = cat.id === "domain" ? t(`rset.domainLabel.${hl.data?.domain}`) : t(`rset.hlCat.${cat.id}.label`);
                                        const desc = t(`rset.hlCat.${cat.id}.desc`);
                                        const style = t(`rset.hlCat.${cat.id}.style`);
                                        return (
                                            <div
                                                key={cat.id}
                                                data-testid={`rset-layer-${cat.id}`}
                                                role="button"
                                                tabIndex={0}
                                                title={`${desc} · ${style}`}
                                                onClick={() => hl.toggleLayer(cat.id)}
                                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); hl.toggleLayer(cat.id); } }}
                                                className="cursor-pointer rounded-lg px-2 py-2 transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none"
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    <span className="h-3.5 w-3.5 shrink-0 rounded-[4px]" style={{ backgroundColor: HL_SWATCH[cat.id], opacity: 0.95 }} />
                                                    <span className="min-w-0 flex-1 truncate text-sm text-t2">{label}</span>
                                                    {cat.count != null && <span className="font-mono-ui text-[11px] text-t4">{cat.count}</span>}
                                                    <Switch checked={!!hl.layers[cat.id]} tabIndex={-1} className="pointer-events-none" />
                                                </div>
                                                <p className="mt-0.5 pl-6 text-[11px] leading-snug text-t4">{desc}</p>
                                                <div className="mt-0.5 flex items-center gap-1.5 pl-6 text-[10px] text-t4/90">
                                                    <span className="inline-block h-2 w-4 rounded-[2px]" style={{ backgroundColor: HL_SWATCH[cat.id], opacity: 0.9 }} />
                                                    <span>{style}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </Group>
        </div>
    );
};

export const ReaderSettings = ({ settings, movement, focus, guided, hl }) => {
    const { t } = useTranslation();
    const [isMobile, setIsMobile] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 639px)");
        const on = () => setIsMobile(mq.matches);
        on();
        mq.addEventListener?.("change", on);
        return () => mq.removeEventListener?.("change", on);
    }, []);

    const trigger = (
        <Button data-testid="reader-settings-button" variant="secondary" size="sm" className="gap-1.5 bg-surface-3/60">
            <Settings2 className="h-4 w-4" />
            <span className="hidden sm:inline">{t("rset.title")}</span>
        </Button>
    );

    if (isMobile) {
        return (
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>{trigger}</SheetTrigger>
                <SheetContent side="bottom" className="max-h-[85vh] rounded-t-2xl border-border bg-surface-1 p-0" data-testid="reader-settings-sheet">
                    <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                        <ScrollText className="h-4 w-4 text-[hsl(var(--primary))]" />
                        <SheetTitle className="font-sans-display text-sm font-semibold text-t1">{t("rset.title")}</SheetTitle>
                        <SheetDescription className="sr-only">{t("rset.subtitle")}</SheetDescription>
                    </div>
                    <SettingsBody settings={settings} movement={movement} focus={focus} guided={guided} hl={hl} t={t} />
                </SheetContent>
            </Sheet>
        );
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            <PopoverContent align="end" className="w-80 border-border bg-surface-1 p-0" data-testid="reader-settings-popover">
                <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                    <ScrollText className="h-4 w-4 text-[hsl(var(--primary))]" />
                    <span className="font-sans-display text-sm font-semibold text-t1">{t("rset.title")}</span>
                </div>
                <SettingsBody settings={settings} movement={movement} focus={focus} guided={guided} hl={hl} t={t} />
            </PopoverContent>
        </Popover>
    );
};
